import React from "react";
import PropTypes from "prop-types";
import ImageCropped from "views_shared/app/javascript/components/ImageCropped";
import { formatDateRange } from "views_shared/app/javascript/utils/dateFormatters";
import SectionCard from "../../SectionCard";

/**
 * EmploymentSection — the shared profile "Employment" section card (chrome; the rows come from the
 * engine). A `<SectionCard>` listing employment rows, each with an optional company avatar, a linked
 * company name, an optional job title, and (when `showDates`) a formatted date range. When there are
 * no rows it shows muted empty-state copy instead of the list. Model-agnostic: it takes pre-adapted
 * display rows and the standard edit-affordance props, which pass straight through to `<SectionCard>`.
 * The section always renders its card and preserves the caller's row order — deciding whether to show
 * it at all, and in what order, is the caller's job.
 *
 * @param {Object} props
 * @param {Array} props.rows - Display rows `{ id, href, imageUrl?, name, jobTitle?, from?, to? }`
 * @param {string} [props.title] - Section title (default "Employment")
 * @param {string} [props.emptyText] - Copy shown when there are no rows (default "No employment added yet.")
 * @param {boolean} [props.showDates] - Whether to render each row's formatted date range (default
 *   false). A row with neither date renders no date line at all — the formatter's bare-"Present"
 *   fallback implied currency the data doesn't claim.
 * @param {boolean} [props.editable] - Whether the viewer may edit this section
 * @param {Function} [props.onEdit] - Click handler for the standard edit pen (shown only when editable)
 * @param {string} [props.editButtonId] - Optional DOM id for the edit pen (stable hook for system tests)
 * @param {string} [props.id] - Optional DOM id (e.g. for system tests / deep links)
 */
function EmploymentSection({
  rows,
  title = "Employment",
  emptyText = "No employment added yet.",
  showDates = false,
  editable = false,
  onEdit = null,
  editButtonId = undefined,
  id = undefined,
}) {
  return (
    <SectionCard id={id} title={title} editable={editable} onEdit={onEdit} editButtonId={editButtonId}>
      {rows.length === 0 ? (
        <div className="text-muted">{emptyText}</div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {rows.map((row) => (
            <div key={row.id} className="d-flex flex-row align-items-start">
              {row.imageUrl && (
                <ImageCropped
                  wrapperClasses="me-3 rounded"
                  src={row.imageUrl}
                  alt={row.name}
                  size={67}
                  className="object-contain"
                  fallbackToInitials
                />
              )}
              <div className="overflow-hidden">
                <div className="fw-bold">
                  <a href={row.href}>{row.name}</a>
                  {/* Same badge vocabulary as the featured quote card: the featured job is the one
                      that supplies the profile title, so it must be legible at a glance here too. */}
                  {row.featured && (
                    <span className="qw-featured-badge badge rounded-pill d-inline-flex align-items-center fw-bold paragraph-sm ms-2">
                      <i className="fas fa-star me-1" />
                      Featured
                    </span>
                  )}
                </div>
                {row.jobTitle && <div>{row.jobTitle}</div>}
                {showDates && (row.from || row.to) && (
                  <div className="text-muted small">{formatDateRange(row.from, row.to)}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

EmploymentSection.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      href: PropTypes.string.isRequired,
      imageUrl: PropTypes.string,
      name: PropTypes.string.isRequired,
      jobTitle: PropTypes.string,
      from: PropTypes.string,
      to: PropTypes.string,
      featured: PropTypes.bool,
    }),
  ).isRequired,
  title: PropTypes.string,
  emptyText: PropTypes.string,
  showDates: PropTypes.bool,
  editable: PropTypes.bool,
  onEdit: PropTypes.func,
  editButtonId: PropTypes.string,
  id: PropTypes.string,
};

export default EmploymentSection;
